import React, { createContext, FC, useContext, useEffect, useMemo, useState } from "react";
import { FormikErrors, FormikProps, useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { permitRequestData } from "../data/initialize/permitRequest.data";
import { requestSchema } from "../validations/schema/createRequestPage/permitRequestValidation";
import { convertFileToSendToServer } from '../helpers/convertors';
import { useProjectConfigContext } from "./ProjectConfigContext";
import { MAX_FILE_SIZE, SUPPORTED_FORMATS } from "../validations/errorMessages";
import { IPermitRequest } from "../interface/permitRequest.interface";
import { IFileDataBase64 } from "../interface/fileDataBase64.interface";
import PermitRequestPage from "../pages/permit-request/PermitRequest.page";
import useAddPermitRequestPageApi from "../axios/useAddPermitRequestPage.api";
import { useSetAPRPAlertLoading, useSetAPRPAlertState } from "../stores/add-permit-request-page.store";
import { timeout } from "../helpers/timeout";
import { useTags } from "../hooks/useTags";
import formErrorsLang from "../data/lang/form-errors.lang";
import { useGetUseNewApiApp } from "../stores/app.store";
import convertBase64ToBlob from "../helpers/files/convert-base64-to-blob";
import useFilesApi from "../axios/useFiles.api";
import {
    useCloseSaveReqConfirmModal, useOpenSaveReqConfirmModal, useSelectSaveReqConfirmModalData,
    useSetIsLoadingSaveReqConfirmModal, useSetSavePermitProcessResult, useSetStepSaveReqConfirmModal
} from "../stores/saveReqConfirmModal.store";
import { IConfirmationModal } from "../interface/confirmationModal.interfase";
import addRequestPageLang from "../data/lang/add-request-page.lang";
import PermitType from "../types/permit.type";
import { normalizeValue } from "../helpers/normalize-value";


type PermitRequestContextType = {
    handlePreSendPermitRequest: () => Promise<void>,
    handleSendPermitRequest: () => Promise<void>,
    handleChange: (event: any) => void,
    handleCloseConfirmModal: () => void,
    Formik: FormikProps<IPermitRequest>,
    handleChangeFile: (inputName: string, fileName: string, file: File) => Promise<void>,
    houseAndAppDes: boolean,
    streetDes: boolean,
    sethouseAndAppDes: (value: boolean) => void,
    setStreetDes: (value: boolean) => void,
    handleOnChangeStreet: (streetId: string | number) => void,
}

const errorToLabelsMap = {
    'personDetails.streetId': 'form.personal-details-section.privateNameLabel',
    'personDetails.familyName': 'form.personal-details-section.familyNameLabel',
    'personDetails.id': 'form.personal-details-section.idLabel',
    'personDetails.scanId': 'form.personal-details-section.personFileLabelWithAdditionalFile',
    'personDetails.additionalPersonIdFile': 'form.personal-details-section.additionalPersonIdFileLabel',
    'contactDetails.streetId': 'form.contact-details-section.streetLabel',
    'contactDetails.house': 'form.contact-details-section.houseLabel',
    'contactDetails.appartment': 'form.contact-details-section.apartmentLabel',
    'contactDetails.email': 'form.contact-details-section.emailLabel',
    'contactDetails.mobile': 'form.contact-details-section.mobileLabel',
    'contactDetails.phone': 'form.contact-details-section.phoneLabel',
    'carDetails.carId': 'form.car-details-section.carIdLabel',
    'carDetails.scanCarId': 'form.car-details-section.scanCarIdLabel',
    'carDetails.scanWorkStatment': 'form.car-details-section.scanWorkStatmentLabel',
    'carDetails.ownershipCarType': 'form.car-details-section.ownershipCarTypeLabel',
    'craditCard.cardNumber': 'form.cradit-card-section.cardNumberLabel',
    'craditCard.cardExpireDateMM': 'form.cradit-card-section.cardExpireDateMMLabel',
    'craditCard.cardExpireDateYY': 'form.cradit-card-section.cardExpireDateYYLabel',
    'craditCard.cardAddedNumber': 'form.cradit-card-section.cardAddedNumberLabel',
    'craditCard.cardHolderId': 'form.cradit-card-section.cardHolderIdLabel',
    'craditCard.cardHolderName': 'form.cradit-card-section.cardHolderNameLabel',
    'additionalContactDetails.mailMikod': 'form.additional-address-section.postalCodeLabel',
    'additionalContactDetails.mailCity': 'form.additional-address-section.cityLabel',
    'additionalContactDetails.mailStreet': 'form.additional-address-section.streetLabel',
    'additionalContactDetails.mailHouse': 'form.additional-address-section.houseLabel',
    'additionalContactDetails.mailAppartment': 'form.additional-address-section.apartmentLabel',
};

export const PermitRequestContext = createContext<PermitRequestContextType | undefined>(undefined);

export const PermitRequestProvider: FC = ({ }) => {

    const navigate = useNavigate();
    const { uploadFilesApi } = useFilesApi();
    const useNewApi = useGetUseNewApiApp();
    const { addPermitRequestApi, calculatePaymentApi } = useAddPermitRequestPageApi();
    const { projectConfigState, authState } = useProjectConfigContext();


    const [houseAndAppDes, sethouseAndAppDes] = useState(false);
    const [streetDes, setStreetDes] = useState(false);
    const [initDataLoaded, setInitDataLoaded] = useState(false);


    const setAlertLoading = useSetAPRPAlertLoading();
    const setAlertState = useSetAPRPAlertState();

    const { step } = useSelectSaveReqConfirmModalData();

    const moveModalStep = useSetStepSaveReqConfirmModal();
    const openSendReqConfirmModal = useOpenSaveReqConfirmModal();
    const closeModal = useCloseSaveReqConfirmModal();
    const setModalIsLoading = useSetIsLoadingSaveReqConfirmModal();
    const setSavePermitProcessResult = useSetSavePermitProcessResult();

    const generateTag = useTags();
    const invalidFormMessage = generateTag(formErrorsLang, 'invalid-form');

    const projectId = projectConfigState.projectId;

    const Formik = useFormik<IPermitRequest>({
        initialValues: { ...permitRequestData, projectId: projectId },
        validateOnMount: true,
        validateOnChange: false,
        validationSchema: requestSchema,
        validateOnBlur: true,
        onSubmit: () => { },
    });

    const { errors, values, isValid, setFieldValue, setFieldTouched, validateForm } = Formik;

    useEffect(() => {
        loadingInitFormData();
    }, [projectConfigState]);

    useEffect(() => {
        handleCalPaymentAmount();
    }, [values.permitType]);

    const handleChange = useMemo(() => {
        return (event) => {
            setFieldValue(event.target.name, event.target.value);
        };
    }, []);

    async function handlePreSendPermitRequest() {

        setAlertLoading();

        await timeout(750);
        const formErrors = await validateForm(values);

        if (!isValid) {

            setAllKeysInObjectAsToutch('', formErrors);

            let detailsFirstErr = '';
            const firstError = getFirstError('', formErrors);

            if (firstError) {
                const [firstErrorKey, firstErrorValue] = firstError;

                const firstKeyErr = generateTag(addRequestPageLang, firstErrorKey);
                const firstErrMsg = generateTag(formErrorsLang, firstErrorValue);

                detailsFirstErr = `${firstKeyErr}: ${firstErrMsg}`;
            }

            setAlertState('hasAlert', `${invalidFormMessage} (${detailsFirstErr})`);
            console.log(`errors: ${JSON.stringify(errors)}`);
            console.log(`values: ${JSON.stringify(values)}`);
            return;
        }

        setAlertState('noAlert');
        openSendReqConfirmModal();
    }

    async function handleSendPermitRequest() {

        setModalIsLoading(true);

        const bodyToSend = { ...values } as IPermitRequest;

        moveModalStep('uploading-files');
        const preSendFileRes = await handlePreSendFiles(bodyToSend);

        const requestWithFilesNames = preSendFileRes.bodyToSend;
        const isPreSendFileResSuccess = preSendFileRes.isSuccess;
        const isPreSendFileMsg = preSendFileRes.message;

        if (!isPreSendFileResSuccess) {

            setAlertState('hasAlert', isPreSendFileMsg);

            moveModalStep('confirm');
            closeModal();
            return;
        }

        moveModalStep('uploading-request');
        const res = await addPermitRequestApi(requestWithFilesNames);

        const success = res.success;
        const msg = res.apiError.displayMessage || '';

        if (!success) {

            setAlertState('hasAlert', msg);

            moveModalStep('confirm');
            closeModal();
            return;
        }

        const confirmationData: IConfirmationModal = {
            asmachta: res.asmachta,
            permitRequestId: res.permitRequestId,
            amountPayed: res.amountPayed,
            hasReceipt: res.hasReceipt,
            receiptUrl: res.receiptUrl,
        }

        setSavePermitProcessResult(confirmationData);
        moveModalStep('finished');
        setModalIsLoading(false);
        return;
    }

    function getFirstError(prevParants: string, obj: FormikErrors<IPermitRequest>): [string, string] | null {

        for (const key in obj) {

            if (!obj.hasOwnProperty(key))
                continue;

            const superKey = prevParants ? `${prevParants}.${key}` : key;
            const value = obj[key];
            if (typeof value === 'object' && !Array.isArray(value) && value !== null) {

                const nestedError = getFirstError(superKey, value);
                if (nestedError) {
                    return nestedError;
                }
            } else if (typeof value === 'string') {

                const formatedKey = errorToLabelsMap[superKey] ? errorToLabelsMap[superKey] : superKey;

                return [formatedKey, value];
            }
        }

        return null;
    }

    function setAllKeysInObjectAsToutch(prevParants: string, obj: FormikErrors<IPermitRequest>) {

        for (const key in obj) {

            if (!obj.hasOwnProperty(key))
                continue;

            const superKey = prevParants ? `${prevParants}.${key}` : key;
            const value = obj[key];

            if (typeof value === 'object' && !Array.isArray(value) && value !== null) {

                setAllKeysInObjectAsToutch(superKey, value);
            } else {
                setFieldTouched(superKey, true, false);
            }

        }
    }

    async function handleChangeFile(inputName: string, fileName: string, file: File) {

        const fileDataBase64: IFileDataBase64 = {
            content: '',
            name: '',
            size: 0,
            contentType: '-1'
        }

        if (!file) {

            setFieldValue(inputName, fileDataBase64, true);
            return;
        }

        if (file?.size > MAX_FILE_SIZE) {

            //result.contentType = MAX_FILE_SIZE + 100;
            setFieldValue(inputName, fileDataBase64, true);
            return;
        }

        if (!SUPPORTED_FORMATS.includes(file?.type)) {

            fileDataBase64.contentType = file.type;
            setFieldValue(inputName, fileDataBase64, true);
            return;
        }


        try {

            const result = await convertFileToSendToServer(file, fileName);
            setFieldValue(inputName, result, true);
        } catch (error) {

            setFieldValue(inputName, {}, true);
        }
    }

    async function handlePreSendFiles(bodyToSend: IPermitRequest): Promise<{
        isSuccess: boolean,
        message: string,
        bodyToSend: IPermitRequest
    }> {

        const result = {
            isSuccess: true,
            message: '',
            bodyToSend: bodyToSend
        }

        if (!useNewApi) {
            return result;
        }

        const modifiedBody = { ...bodyToSend };
        const personId = bodyToSend.personDetails.id;

        // Convert and upload files first
        const fileFields = [] as { name: string, file: Blob }[];
        const allFileDataBase64s: IFileDataBase64[] = [
            bodyToSend.personDetails.scanId,
            bodyToSend.personDetails.additionalPersonIdFile,
            bodyToSend.carDetails.scanWorkStatment,
            bodyToSend.carDetails.scanCarId,
            bodyToSend.extraFiles.extraFile1,
            bodyToSend.extraFiles.extraFile2,
            bodyToSend.extraFiles.extraFile3,
            bodyToSend.extraFiles.extraFile4,
            bodyToSend.extraFiles.extraFile5,
        ];

        allFileDataBase64s.forEach((fileDataBase64) => {

            if (fileDataBase64?.content && fileDataBase64.size > 50) {

                console.log(`Preparing file for upload: ${fileDataBase64.name}, size: ${fileDataBase64.size}, contentType: ${fileDataBase64.contentType}`);
                const blob = convertBase64ToBlob(fileDataBase64.content, fileDataBase64.contentType);
                fileFields.push({ name: fileDataBase64.name, file: blob });
            }
        });


        console.log('Request to upload files:', fileFields);

        const responseUploadFiles = await uploadFilesApi(personId, fileFields);

        if (!responseUploadFiles.isSuccess) {

            return {
                isSuccess: false,
                message: responseUploadFiles.message,
                bodyToSend: bodyToSend
            };
        }

        responseUploadFiles.list.forEach(fileResponse => {

            if (fileResponse.originalFileName === 'scanId') {
                modifiedBody.personDetails.scanId.name = fileResponse.fileName;
                modifiedBody.personDetails.scanId.content = '';
            }

            if (fileResponse.originalFileName === 'additionalPersonIdFile') {
                modifiedBody.personDetails.additionalPersonIdFile.name = fileResponse.fileName;
                modifiedBody.personDetails.additionalPersonIdFile.content = '';
            }

            if (fileResponse.originalFileName === 'scanCarId') {
                modifiedBody.carDetails.scanCarId.name = fileResponse.fileName;
                modifiedBody.carDetails.scanCarId.content = '';
            }

            if (fileResponse.originalFileName === 'scanWorkStatment') {
                modifiedBody.carDetails.scanWorkStatment.name = fileResponse.fileName;
                modifiedBody.carDetails.scanWorkStatment.content = '';
            }

            if (fileResponse.originalFileName === 'extraFile1') {
                modifiedBody.extraFiles.extraFile1.name = fileResponse.fileName;
                modifiedBody.extraFiles.extraFile1.content = '';
            }

            if (fileResponse.originalFileName === 'extraFile2') {
                modifiedBody.extraFiles.extraFile2.name = fileResponse.fileName;
                modifiedBody.extraFiles.extraFile2.content = '';
            }

            if (fileResponse.originalFileName === 'extraFile3') {
                modifiedBody.extraFiles.extraFile3.name = fileResponse.fileName;
                modifiedBody.extraFiles.extraFile3.content = '';
            }

            if (fileResponse.originalFileName === 'extraFile4') {
                modifiedBody.extraFiles.extraFile4.name = fileResponse.fileName;
                modifiedBody.extraFiles.extraFile4.content = '';
            }

            if (fileResponse.originalFileName === 'extraFile5') {
                modifiedBody.extraFiles.extraFile5.name = fileResponse.fileName;
                modifiedBody.extraFiles.extraFile5.content = '';
            }
        });

        return { isSuccess: true, message: 'קובץ עולה בהצלחה', bodyToSend: modifiedBody };
    }

    function loadingInitFormData() {

        if (initDataLoaded || !projectConfigState.hadProjectConfigLoaded) return;

        const initFormData = projectConfigState.addRequestPageConfig.initFormData;

        for (const key in initFormData) {

            if (!initFormData.hasOwnProperty(key)) {
                continue;
            }
            
            const value = initFormData[key];
            setFieldValue(key, normalizeValue(value));
        }

        setFieldValue('projectId', projectId);

        console.log('Initial form data loaded:', initFormData);
        setInitDataLoaded(true);
    }

    async function handleCalPaymentAmount() {

        const loggin = authState.loggin;

        if (!loggin) {
            return;
        }

        const response = await calculatePaymentApi(values.permitType, values.contactDetails.streetId);

        if (!response.isSuccess) {

            setAlertState('hasAlert', response.message);
            return;
        }

        const paymentAmount = response.amount;
        setFieldValue('paymentAmount', paymentAmount);
    }

    function handleCloseConfirmModal() {

        closeModal();

        if (step === 'finished') {
            navigate(`/home/${projectId}`);
        }

    }

    function handleOnChangeStreet(streetId: string | number) {

        const streetIdList18 = ['9002', '9007', '9009', '9011', '9013'];
        setFieldValue('contactDetails.streetId', streetId);


        if (projectId === 18) {

            if (streetIdList18.includes(String(streetId)))
                setFieldValue('permitType', PermitType.NONE_RESIDENT);
            else
                setFieldValue('permitType', PermitType.REGULAR);
        }

    }

    return (
        <PermitRequestContext.Provider value={
            {
                Formik, handleChange, handleCloseConfirmModal, handleChangeFile,
                handlePreSendPermitRequest, handleSendPermitRequest,
                houseAndAppDes, streetDes, setStreetDes, sethouseAndAppDes,
                handleOnChangeStreet,
            }
        }>
            <PermitRequestPage />
        </PermitRequestContext.Provider>
    );

}

export const usePermitRequestContext = () => useContext(PermitRequestContext);